import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const stats = [
  { number: '300+', label: 'Website Projects' },
  { number: '93+', label: 'Social Media Accounts' },
  { number: '500+', label: 'Happy Clients' },
  { number: '6+', label: 'Years Experience' },
];

const services = [
  { icon: '📱', title: 'Social Media Marketing', desc: 'Grow your brand presence across Facebook, Instagram, and more with targeted campaigns.', path: '/services/social-media-marketing' },
  { icon: '🔍', title: 'Social Media Optimization', desc: 'Optimize your social profiles to increase organic reach and audience engagement.', path: '/services/social-media-optimization' },
  { icon: '💬', title: 'WhatsApp Marketing', desc: 'Reach thousands of customers instantly with personalized WhatsApp campaigns.', path: '/services/whatsapp-marketing' },
  { icon: '🎯', title: 'Google Ads', desc: 'Drive quality traffic and leads with expertly managed Google Ads campaigns.', path: '/services/google-ads' },
  { icon: '📊', title: 'Meta Ads', desc: 'Maximize ROI with data-driven Facebook and Instagram advertising strategies.', path: '/services/meta-ads' },
  { icon: '🌐', title: 'Website Development', desc: 'Build high-performance, conversion-focused websites that represent your brand.', path: '/services/website-development' },
  { icon: '🛒', title: 'E-Commerce Development', desc: 'Launch powerful online stores with seamless shopping experiences.', path: '/services/ecommerce-development' },
  { icon: '🎨', title: 'Graphic Design', desc: 'Create stunning visuals that communicate your brand story effectively.', path: '/services/graphic-design' },
];

const whyUs = [
  { icon: '⚡', title: 'Result-Driven Approach', desc: 'We focus on measurable outcomes — leads, conversions, and real revenue growth.' },
  { icon: '🔬', title: 'Data & Strategy', desc: 'Every decision is backed by analytics, insights, and proven digital strategies.' },
  { icon: '🤝', title: 'Transparent Communication', desc: 'Regular reports, clear timelines, and honest updates — always.' },
  { icon: '🏆', title: 'Trusted by 500+ Brands', desc: 'From startups to established businesses, we\'ve helped hundreds scale.' },
];

const testimonials = [
  { name: 'Rahul Sharma', role: 'Business Owner', text: 'Digital Media Solution transformed our online presence. Our leads tripled within 3 months!', rating: 5 },
  { name: 'Priya Patel', role: 'E-Commerce Founder', text: 'The team is incredibly professional. Our website looks stunning and converts brilliantly.', rating: 5 },
  { name: 'Amit Desai', role: 'Restaurant Owner', text: 'Our Google profile now dominates local searches. Footfall has increased significantly.', rating: 5 },
];

export default function Home() {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.1 });
    document.querySelectorAll('.animate-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="home">
      {/* HERO */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg">
          <div className="hero-grid" />
          <div className="hero-orb orb-1" />
          <div className="hero-orb orb-2" />
        </div>
        <div className="container hero-content">
          <span className="section-tag">Mumbai's #1 Digital Agency</span>
          <h1>
            We Grow Your<br />
            Business <span>Online</span>
          </h1>
          <p>At Digital Media Solution, we help brands scale online with result-driven SEO, Social Media Marketing, PPC, Website Development, and creative content solutions designed to increase visibility, generate leads, and drive real growth.</p>
          <div className="hero-cta">
            <Link to="/contact" className="btn-primary">Get Free Consultation</Link>
            <Link to="/services" className="btn-outline">Our Services</Link>
          </div>
          <div className="hero-socials">
            <span>Connect with us:</span>
            <a href="https://www.facebook.com/dmstechnologies" target="_blank" rel="noreferrer">Facebook</a>
            <a href="https://www.instagram.com/dms_technologies/" target="_blank" rel="noreferrer">Instagram</a>
            <a href="https://wa.me/919370115694" target="_blank" rel="noreferrer">WhatsApp</a>
            <a href="https://share.google/b2XElxUDz7fwlsfpW" target="_blank" rel="noreferrer">Google</a>
          </div>
        </div>
       
      </section>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((s, i) => (
              <div className="stat-card animate-on-scroll" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="stat-number">{s.number}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="about-snippet">
        <div className="container about-grid">
          <div className="about-text animate-on-scroll">
            <span className="section-tag">About Digital Media Solution</span>
            <h2 className="section-title">Your Digital <span>Growth Partner</span></h2>
            <p>Digital Media Solution is a result-driven digital marketing and website development company dedicated to helping businesses grow and succeed in the digital world. We specialize in creating powerful online strategies that increase brand visibility, attract the right audience, and generate consistent leads.</p>
            <p>Our team combines creativity, advanced technology, and data-driven insights to deliver customized solutions tailored to your business goals.</p>
            <div className="about-cta">
              <Link to="/about" className="btn-primary">Learn More About Us</Link>
            </div>
          </div>
          <div className="about-visual animate-on-scroll">
            <div className="visual-card">
              <div className="vc-inner">
                <div className="vc-stat"><span>500+</span><p>Happy Clients</p></div>
                <div className="vc-stat"><span>6+</span><p>Years Experience</p></div>
                <div className="vc-badge">Trusted by Businesses Across the Globe</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">What We Do</span>
            <h2 className="section-title">Our <span>Services</span></h2>
            <p>Complete digital solutions under one roof — from strategy to execution.</p>
          </div>
          <div className="services-grid">
            {services.map((s, i) => (
              <Link to={s.path} className="service-card animate-on-scroll" key={i} style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="sc-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <span className="sc-arrow">→</span>
              </Link>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Link to="/services" className="btn-outline">View All Services</Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="why-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Why Digital Media Solution</span>
            <h2 className="section-title">Why Choose <span>Us?</span></h2>
          </div>
          <div className="why-grid">
            {whyUs.map((w, i) => (
              <div className="why-card animate-on-scroll" key={i} style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="why-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header animate-on-scroll">
            <span className="section-tag">Client Love</span>
            <h2 className="section-title">What Our <span>Clients Say</span></h2>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((t, i) => (
              <div className="testimonial-card animate-on-scroll" key={i}>
                <div className="t-stars">{'★'.repeat(t.rating)}</div>
                <p>"{t.text}"</p>
                <div className="t-author">
                  <div className="t-avatar">{t.name[0]}</div>
                  <div>
                    <strong>{t.name}</strong>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="cta-banner">
        <div className="cta-bg" />
        <div className="container cta-content animate-on-scroll">
          <h2>Ready to Grow Your Business <span>Online?</span></h2>
          <p>Get a free consultation with our digital marketing experts today.</p>
          <div className="cta-actions">
            <a href="tel:+919370115694" className="btn-primary">📞 Call Now: +91 9370115694</a>
            <Link to="/contact" className="btn-outline">Send a Message</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
